import { supabase } from '../lib/supabase';

class RealtimeMonitoringService {
  constructor() {
    this.alertSubscription = null;
    this.listeners = new Set();
  }

  /**
   * Subscribe to real-time admin alerts
   * @param {Function} callback - Function to call when new alerts arrive
   * @returns {Function} - Unsubscribe function
   */
  subscribeToAlerts(callback) {
    if (!callback || typeof callback !== 'function') {
      console.error('RealtimeMonitoringService: callback must be a function');
      return () => {};
    }

    this.listeners?.add(callback);

    // Only create one subscription for all listeners
    if (!this.alertSubscription) {
      this.alertSubscription = supabase?.channel('admin_alerts_channel')?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_alerts'
          },
          (payload) => {
            const newAlert = payload?.new;
            if (newAlert) {
              // Notify all listeners
              this.listeners?.forEach(listener => {
                try {
                  listener(newAlert);
                } catch (error) {
                  console.error('Error in alert listener:', error);
                }
              });
            }
          }
        )?.subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ Real-time monitoring connected');
          } else if (status === 'CLOSED') {
            console.log('❌ Real-time monitoring disconnected');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Real-time monitoring error');
          }
        });
    }

    // Return unsubscribe function
    return () => {
      this.listeners?.delete(callback);
      
      // If no more listeners, unsubscribe from channel
      if (this.listeners?.size === 0 && this.alertSubscription) {
        supabase?.removeChannel(this.alertSubscription);
        this.alertSubscription = null;
        console.log('🔌 Real-time monitoring unsubscribed');
      }
    };
  }

  /**
   * Fetch unread alerts count
   */
  async getUnreadCount() {
    try {
      const { count, error } = await supabase?.from('admin_alerts')?.select('*', { count: 'exact', head: true })?.eq('is_read', false)?.eq('is_dismissed', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  /**
   * Fetch recent alerts with pagination
   */
  async getRecentAlerts(limit = 20, offset = 0) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.select('*')?.eq('is_dismissed', false)?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching recent alerts:', error);
      return [];
    }
  }

  /**
   * Get alerts by severity
   */
  async getAlertsBySeverity(severity, limit = 10) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.select('*')?.eq('severity', severity)?.eq('is_dismissed', false)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching alerts by severity:', error);
      return [];
    }
  }

  /**
   * Get alerts by category
   */
  async getAlertsByCategory(category, limit = 10) {
    try {
      const { data, error } = await supabase?.from('admin_alerts')?.select('*')?.eq('category', category)?.eq('is_dismissed', false)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching alerts by category:', error);
      return [];
    }
  }

  /**
   * Mark alert as read
   */
  async markAsRead(alertId) {
    try {
      const { error } = await supabase?.from('admin_alerts')?.update({ 
          is_read: true, 
          read_at: new Date()?.toISOString() 
        })?.eq('id', alertId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      throw error;
    }
  }

  /**
   * Mark alert as dismissed
   */
  async dismissAlert(alertId) {
    try {
      const { error } = await supabase?.from('admin_alerts')?.update({ 
          is_dismissed: true, 
          dismissed_at: new Date()?.toISOString() 
        })?.eq('id', alertId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error dismissing alert:', error);
      throw error;
    }
  }

  /**
   * Mark all unread alerts as read
   */
  async markAllAsRead() {
    try {
      const { error } = await supabase?.from('admin_alerts')?.update({ 
          is_read: true, 
          read_at: new Date()?.toISOString() 
        })?.eq('is_read', false)?.eq('is_dismissed', false);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  }

  /**
   * Get alert statistics
   */
  async getAlertStats() {
    try {
      const [totalCount, unreadCount, criticalCount, urgentCount] = await Promise.all([
        supabase?.from('admin_alerts')?.select('*', { count: 'exact', head: true })?.eq('is_dismissed', false),
        supabase?.from('admin_alerts')?.select('*', { count: 'exact', head: true })?.eq('is_read', false)?.eq('is_dismissed', false),
        supabase?.from('admin_alerts')?.select('*', { count: 'exact', head: true })?.eq('severity', 'critical')?.eq('is_dismissed', false),
        supabase?.from('admin_alerts')?.select('*', { count: 'exact', head: true })?.eq('severity', 'urgent')?.eq('is_dismissed', false)
      ]);

      return {
        total: totalCount?.count || 0,
        unread: unreadCount?.count || 0,
        critical: criticalCount?.count || 0,
        urgent: urgentCount?.count || 0
      };
    } catch (error) {
      console.error('Error fetching alert stats:', error);
      return { total: 0, unread: 0, critical: 0, urgent: 0 };
    }
  }

  /**
   * Cleanup old dismissed alerts (call periodically)
   */
  async cleanupOldAlerts() {
    try {
      const { data, error } = await supabase?.rpc('cleanup_old_alerts');

      if (error) throw error;
      return data || 0;
    } catch (error) {
      console.error('Error cleaning up old alerts:', error);
      return 0;
    }
  }
}

export const realtimeMonitoringService = new RealtimeMonitoringService();